import { DimensionStatus } from "../../store/dimension.status";
import type { InputDimensionValue } from "../../store/dimension.value";
import type { RequestParameters } from "../../types/common";
import type { DimensionConfiguration } from "../../types/dimensions";
import {
    AdditionalInputComponentTypes,
    InputComponentTypes,
} from "../../types/inputs";
import { InputComponent } from "../common/Input";
import type { InputComponentProps } from "../common/Input";
import { TextAreaComponent } from "../common/TextArea";

export type InputDimensionComponentTypes =
    | InputComponentTypes
    | AdditionalInputComponentTypes;

export interface InputDimensionConfiguration extends DimensionConfiguration {
    validation?: InputValidationOptions;
    type: InputDimensionComponentTypes;
}

export interface InputValidationOptions {
    regex?: {
        expression: string;
        errorMessage: string;
    };
}

export const buildRequestParameters = (
    dim: string,
    type: InputDimensionComponentTypes,
    requestParameterMap: Record<string, string> | undefined,
    v: InputDimensionValue,
): RequestParameters => {
    const value = processValue(type, v.value);
    return { [requestParameterMap?.value ?? dim]: value };
};

export const processValue = (
    type: InputDimensionComponentTypes,
    value: string,
): string => {
    if (type === InputComponentTypes.PERCENT) {
        return (parseFloat(value) / 100).toString();
    }
    return value;
};

export const reverseProcessValue = (
    type: InputDimensionComponentTypes,
    value: string,
): string => {
    if (type === InputComponentTypes.PERCENT) {
        return (parseFloat(value) * 100).toString();
    }
    return value;
};

export const processStatus = (
    value: string,
    validationOptions: InputValidationOptions = {},
    optional?: boolean,
): [DimensionStatus, string] => {
    if (value.length === 0 && !optional) {
        return [DimensionStatus.EMPTY, ""];
    }
    if (validationOptions.regex) {
        if (value.length === 0 && optional) {
            return [DimensionStatus.SUCCESS, ""];
        }
        const regexp = new RegExp(validationOptions.regex.expression);
        if (!regexp.test(value)) {
            return [
                DimensionStatus.INVALID,
                validationOptions.regex.errorMessage,
            ];
        }
    }
    return [DimensionStatus.SUCCESS, ""];
};

export const getInputComponent = (
    props: InputComponentProps,
    type: InputDimensionComponentTypes,
): JSX.Element => {
    const defaultInputComponent = <InputComponent {...props} />;
    const inputComponentMap: {
        [key in AdditionalInputComponentTypes]: JSX.Element;
    } = {
        [AdditionalInputComponentTypes.TEXT_AREA]: (
            <TextAreaComponent {...props} />
        ),
    };
    return inputComponentMap[type] || defaultInputComponent;
};
